import React, { forwardRef, useState } from 'react';

import { MdClose } from 'react-icons/md';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import {
  DialogEditItem,
  FormControlMui,
  ProductInfo,
  ProductsSelected,
  FlexContainer,
  CloseButton,
} from './OrderItemEdit.style';
import { Button } from '../../components/button/Button';
import { IProduct } from '../../interfaces/IProduct';
import { useProductService } from '../../services/useProductService';
import { Autocomplete, DialogTitle, FormHelperText, Grid, TextField } from '@mui/material';
import IFormError from '../../interfaces/IFormError';
import { useValidateSchema } from '../../services/useValidateSchema';
import { useToastr } from '../../hooks/useToastr';
import * as yup from 'yup';
import { IOrderItem } from '../../interfaces/IOrderItem';
import { useOrderItemService } from '../../services/useOrderItemService';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { useLocalStorage } from '../../services/useLocalStorage';
import { BackdropCustom } from '../../components/backdrop/Backdrop';
import { TotalsValues } from './OrderItemList.style';
interface IProps {
  openDialog: boolean;
  orderId: string;
  orderBranchOfficeId: string;
  id?: string;
  handleClose: () => void;
  itemsUpdated?(): void;
  handleListItems?(): void;
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  decimalScale: number;
}

const NumericFormatCustom = forwardRef<NumericFormatProps, CustomProps>(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={values => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator={'.'}
      decimalScale={props.decimalScale}
      decimalSeparator=","
      valueIsNumericString
    />
  );
});

const OrdemItemEdit: React.FC<IProps> = ({
  openDialog,
  orderId,
  orderBranchOfficeId,
  id,
  handleClose,
  itemsUpdated,
  handleListItems,
}) => {
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState<IProduct | null>(null);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [quantity, setQuantity] = useState('');
  const [offerPercentage, setOfferPercentage] = useState('');
  const [additionPercentage, setAdditionPercentage] = useState('');
  const [errors, setErrors] = useState<IFormError>({});
  const [orderItem, setOrderItem] = useState<IOrderItem | null>(null);
  const [price, setPrice] = useState(0);
  const [totalValue, setTotalValue] = useState(0);

  const [perPage] = useState(20000);
  const [currentPage] = useState(0);
  const [orderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField] = useState<string>('description');
  const [decimalScale, setDecimalScale] = useState(0);
  const [filterByCode, setFilterByCode] = useState(false);

  const productService = useProductService();
  const orderItemService = useOrderItemService();
  const localStorage = useLocalStorage();
  const validateSchema = useValidateSchema();

  const toastr = useToastr();

  const schema = yup.object({
    productId: yup.string().required('Campo obrigatório'),
    quantity: yup
      .string()
      .required('Campo obrigatório')
      .test('len', 'Quantidade não informada.', val => !!val && !!(Number(val) > 0.01)),
    offerPercentage: yup
      .string()
      .test('len', 'Percentual não pode ser maior que 100', val => !!val && !!(Number(val) < 100)),
  });

  React.useEffect(() => {
    const filter = localStorage.getItem('@PEDIDO-WEB:filterProductByCode');
    if (filter) {
      setFilterByCode(filter);
    }
    if (openDialog && id) {
      loadOrderItem();
    }
    if (openDialog) {
      listProducts();
    }
  }, [openDialog]);

  React.useEffect(() => {
    const discount = parseFloat(offerPercentage) || 0;
    const addition = parseFloat(additionPercentage) || 0;

    let price = orderItem?.grossPrice || 0;
    if (discount > 0 && !addition) {
      price = price * (1 - discount / 100);
    }
    if (addition > 0 && !discount) {
      price = price * (1 + addition / 100);
    }
    if (discount && addition) {
      price = price * (1 + addition / 100 - 1 / discount);
    }
    const itemQuantity = parseFloat(quantity) || 0;
    const taxBase = orderItem?.price ? orderItem.price * itemQuantity : 0;
    const ICMSST_Value = orderItem?.ICMSST_Value || 0;
    const ICMSST_Percent = taxBase ? ICMSST_Value / taxBase : 0;
    const IPIPercent = orderItem?.IPI_Percent ? orderItem?.IPI_Percent / 100 : 0;
    price = price * (1 + (IPIPercent + ICMSST_Percent));

    setPrice(price);
    setTotalValue(price * itemQuantity);
  }, [quantity, offerPercentage, additionPercentage]);

  const closeDialog = () => {
    setProduct(null);
    setQuantity('');
    setOrderItem(null);
    setOfferPercentage('');
    setAdditionPercentage('');
    setErrors({});
    handleClose();
  };

  const resetForm = () => {
    setProduct(null);
    setQuantity('');
    setOrderItem(null);
    setOfferPercentage('');
    setAdditionPercentage('');
    setErrors({});
    setFocusById('product-autocomplete');
  };

  const loadOrderItem = async () => {
    setLoading(true);

    await orderItemService
      .listById(`${id}`)
      .then(item => {
        item.product && setProduct(item.product);
        setQuantity(item.quantity > 0 ? String(item.quantity) : '');
        setDecimalScale(item.product?.measurementUnit?.precision || 0);
        setOfferPercentage(item.offerPercentage > 0 ? String(item.offerPercentage) : '');
        setAdditionPercentage(
          item?.additionPercentage && item?.additionPercentage > 0 ? String(item?.additionPercentage) : '',
        );
        setOrderItem(item);
        if (item.price) {
          setPrice(item.price);
          setTotalValue(item.price * item.quantity);
        }
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const listProducts = async (filter = '') => {
    setLoading(true);
    const url = `perPage=${perPage}&currentPage=${
      currentPage + 1
    }&orderBy=${orderField}&orderDirection=${orderDirection}&filter=${filter}&filterByCode=${filterByCode}&hideImage=true&listActive=true`;

    await productService
      .listAll(url)
      .then(result => {
        if (result) {
          setProducts(result.data);
        }
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
        setFocusById('product-autocomplete');
      });
  };

  const listProduct = async (productId: string | null) => {
    if (productId) {
      setLoading(true);
      await productService
        .listProductById(productId)
        .then(result => {
          if (result) {
            setProduct(result);
            setDecimalScale(result?.measurementUnit?.precision || 0);
          }
        })
        .catch(error => {
          toastr.error(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const sortProductsBySearch = (searchTerm: string) => {
    const sorted = products.slice().sort((a, b) => {
      const aText = `${a.sku ?? ''}${a.description ?? ''}`.toLowerCase();
      const bText = `${b.sku ?? ''}${b.description ?? ''}`.toLowerCase();
      const search = searchTerm.toLowerCase();

      const aIndex = aText.indexOf(search);
      const bIndex = bText.indexOf(search);

      const safeA = aIndex === -1 ? Infinity : aIndex;
      const safeB = bIndex === -1 ? Infinity : bIndex;

      // Comparação primária: posição do termo de busca
      if (safeA !== safeB) {
        return safeA - safeB;
      }

      // Comparação secundária: alfabética por SKU
      const skuA = (a.sku ?? '').toLowerCase();
      const skuB = (b.sku ?? '').toLowerCase();
      return skuA.localeCompare(skuB);
    });

    setProducts(sorted);
  };

  const createOrder = async (item: IOrderItem) => {
    setLoading(true);
    await orderItemService
      .create(item)
      .then(() => {
        toastr.success('Item adicionado com sucesso');
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateOrder = async (item: IOrderItem) => {
    setLoading(true);
    await orderItemService
      .update(`${item.id}`, item)
      .then(() => {
        toastr.success('Item atualizado com sucesso');
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleConfirmation = async () => {
    const item: IOrderItem = {
      id,
      orderId,
      sequence: orderItem?.sequence || 0,
      productId: product?.id || undefined,
      product: product || undefined,
      quantity: Number(quantity),
      price: orderItem?.price || 0,
      freightValue: orderItem?.freightValue || 0,
      discountFTZ: orderItem?.discountFTZ || 0,
      IPI_Percent: orderItem?.IPI_Percent || 0,
      IPI_Value: orderItem?.IPI_Value || 0,
      ICMSST_Value: orderItem?.ICMSST_Value || 0,
      ICMS_Percent: orderItem?.ICMS_Percent || 0,
      amount: orderItem?.amount || 0,
      offerPercentage: Number(offerPercentage) || 0,
      additionPercentage: Number(additionPercentage) || 0,
      offerValue: orderItem?.offerValue || 0,
      status: orderItem?.status || undefined,
      errorMessage: orderItem?.errorMessage || '',
    };

    const result = await validateSchema.validate(schema, item);
    if (!result.isValid) {
      setErrors(result.errors ? result.errors : {});
      return;
    }

    if (id) {
      await updateOrder(item);
    } else {
      await createOrder(item);
    }

    itemsUpdated?.();
    handleListItems?.();
    resetForm();
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-br', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };

  const setFocusById = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.focus();
    }
  };

  const selectAutocompleteFocusedOnTab = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      const popup = document.querySelector('[role="listbox"]');
      const input = event.target as HTMLElement;
      const activedescendant = input.getAttribute('aria-activedescendant');
      let toClick: HTMLElement | null = null;
      if (activedescendant) {
        toClick = popup?.querySelector(`#${activedescendant}`) as HTMLElement;
      }
      if (!toClick) {
        toClick = popup?.querySelector('[data-focus="true"]') as HTMLElement;
      }
      if (!toClick) {
        toClick = popup?.querySelector('[aria-selected="true"]') as HTMLElement;
      }
      if (toClick) {
        toClick.click();
        event.preventDefault();
      }
    }
  };

  return (
    <DialogEditItem open={openDialog} onClose={closeDialog}>
      <DialogTitle sx={{ borderBottom: '#efefef solid 1px' }}>Adicionar Produto</DialogTitle>
      <CloseButton onClick={closeDialog}>
        <MdClose />
      </CloseButton>

      <DialogContent>
        <div className="product-area">
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <Autocomplete
                id="product-autocomplete"
                options={products}
                fullWidth
                value={product}
                onKeyDown={event => {
                  selectAutocompleteFocusedOnTab(event);
                }}
                onChange={(_, newValue) => {
                  listProduct(newValue?.id || null);
                  setErrors(prev => ({ ...prev, productId: '' }));
                }}
                onInputChange={(_, value) => {
                  sortProductsBySearch(value);
                }}
                isOptionEqualToValue={(option, value) => {
                  return option.id === value?.id || true;
                }}
                getOptionLabel={option => {
                  if (option.sku) {
                    return !option.derivation
                      ? `${option.sku} ${option.description}`
                      : `${option.sku} ${option.description} ${option.derivation}`;
                  } else {
                    return '';
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Produto"
                    size="small"
                    error={Boolean(errors?.productId)}
                    helperText={errors?.productId || ''}
                  />
                )}
              />

              <Grid item xs={12} md={12} sx={{ marginBottom: '10px' }}>
                {product ? (
                  <ProductsSelected>
                    <div>
                      <img src={`data:image/png;base64,${product?.image}`} alt={product?.sku} width="80px" />
                      <p>
                        {`${product?.sku} - ${product?.description} ${
                          product?.derivation && product?.derivation?.length >= 1 ? `- ${product?.derivation}` : ''
                        } `}{' '}
                      </p>
                    </div>
                  </ProductsSelected>
                ) : (
                  <ProductsSelected>
                    <p>Nenhum produto selecionado</p>
                  </ProductsSelected>
                )}
                {product && (
                  <ProductInfo>
                    <div>
                      <span>
                        <strong>Múltiplo: </strong> <span>{product?.masterPacking || 0}</span>
                      </span>
                      <span>
                        <strong>NCM: </strong> <span>{product?.ncm || ''}</span>
                      </span>
                      <span>
                        <strong>Código de Barras: </strong> <span>{product?.barcode || ''}</span>
                      </span>
                    </div>
                    <div>
                      <span>
                        <strong>Estoque Disponível: </strong>{' '}
                        <span>
                          {new Intl.NumberFormat('pt-br', {
                            minimumFractionDigits: decimalScale,
                            maximumFractionDigits: decimalScale,
                          }).format(
                            product?.productStockBalances?.find(stock => stock.branchOfficeId === orderBranchOfficeId)
                              ?.quantity || 0,
                          )}
                        </span>
                      </span>
                    </div>
                  </ProductInfo>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} md={12} style={{ padding: '8px' }}>
              <FormControlMui sx={{ m: 1, width: '100%' }} size="small" error={!!errors.quantity}>
                <TextField
                  label="Quantidade"
                  style={{ width: '100%' }}
                  value={quantity}
                  onChange={event => {
                    setQuantity(event.target.value);
                    setErrors(prev => ({ ...prev, quantity: '' }));
                  }}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                    inputProps: {
                      decimalScale,
                    },
                  }}
                  size="small"
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      handleConfirmation();
                    }
                  }}
                />
                <FormHelperText>{errors.quantity}</FormHelperText>
              </FormControlMui>
            </Grid>
            <Grid item xs={12} md={12} style={{ padding: '8px' }}>
              <FormControlMui sx={{ m: 1, width: '100%' }} size="small" error={!!errors.offerPercentage}>
                <TextField
                  style={{ width: '100%' }}
                  label="% Oferta"
                  value={offerPercentage}
                  onChange={event => setOfferPercentage(event.target.value)}
                  name="value"
                  size="small"
                  id="distance-miles-input"
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                    inputProps: {
                      decimalScale: 4,
                    },
                  }}
                  variant="outlined"
                  error={!!errors.offerPercentage}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      handleConfirmation();
                    }
                  }}
                />
                <FormHelperText>{errors.offerPercentage}</FormHelperText>
              </FormControlMui>
            </Grid>
            <Grid item xs={12} md={12} style={{ padding: '8px' }}>
              <FormControlMui sx={{ m: 1, width: '100%' }} size="small">
                <TextField
                  style={{ width: '100%' }}
                  label="% Acréscimo"
                  value={additionPercentage}
                  onChange={event => setAdditionPercentage(event.target.value)}
                  name="value"
                  size="small"
                  id="distance-miles-input"
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                    inputProps: {
                      decimalScale: 4,
                    },
                  }}
                  variant="outlined"
                  onKeyDown={event => {
                    if ((event.key === 'Tab' && !event.shiftKey) || event.key === 'Enter') {
                      handleConfirmation();
                    }
                  }}
                />
                <FormHelperText>{errors.additionPercentage}</FormHelperText>
              </FormControlMui>
            </Grid>
          </Grid>
          {orderItem?.price && orderItem?.price > 0 ? (
            <FlexContainer>
              <TotalsValues>
                <strong>Preço Unit. Estimado</strong>
                <div>{formatCurrency(price)}</div>
              </TotalsValues>

              <TotalsValues>
                <strong>Total Estimado</strong>
                <div>{formatCurrency(totalValue)}</div>
              </TotalsValues>
            </FlexContainer>
          ) : (
            ''
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} color="inherit">
          Fechar
        </Button>
        <Button variant="contained" onClick={handleConfirmation}>
          Confirmar
        </Button>
      </DialogActions>
      {loading && <BackdropCustom />}
    </DialogEditItem>
  );
};

export { OrdemItemEdit };
